import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Paperclip, Upload, Eye, Trash2, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ComprovanteCell — célula compacta usada em tabelas (admin + vendor) para:
 *  - Ver quantos comprovantes existem num pagamento específico
 *  - Anexar novo arquivo (PDF/imagem até 10MB)
 *  - Visualizar (preview/download) e remover comprovantes existentes
 *
 * Props:
 *  - romaneioId: string
 *  - paymentType: "comissao" | "pagamento"
 *  - paymentIndex: number  (índice no historico_comissoes ou historico_pagamentos)
 *  - canUpload?: boolean (default true)
 *  - canDelete?: boolean (default true)
 *  - onChange?: () => void  (callback quando algo muda)
 *  - compact?: boolean (default true)
 */
export function ComprovanteCell({ romaneioId, paymentType, paymentIndex, canUpload = true, canDelete = true, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showList, setShowList] = useState(false);
  const [visualizandoId, setVisualizandoId] = useState(null);
  const inputRef = useRef(null);

  const carregar = async () => {
    try {
      setLoading(true);
      const resp = await axios.get(`${API}/romaneios/${romaneioId}/comprovantes`, {
        params: { payment_type: paymentType, payment_index: paymentIndex },
      });
      setItems(resp.data || []);
    } catch (e) {
      // 403 ou outros — silenciosamente esconde
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); }, [romaneioId, paymentType, paymentIndex]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo maior que 10MB');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('payment_type', paymentType);
      fd.append('payment_index', String(paymentIndex));
      fd.append('descricao', '');
      fd.append('file', file);
      await axios.post(`${API}/romaneios/${romaneioId}/comprovantes`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Comprovante anexado');
      await carregar();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao anexar comprovante');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover este comprovante?')) return;
    try {
      await axios.delete(`${API}/comprovantes/${id}`);
      toast.success('Comprovante removido');
      await carregar();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao remover');
    }
  };

  if (loading) {
    return <Loader2 className="w-4 h-4 animate-spin text-gray-400 mx-auto" />;
  }

  return (
    <>
      <div className="inline-flex items-center gap-1.5">
        {items.length > 0 ? (
          <button
            onClick={() => setShowList(!showList)}
            className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200"
            title="Ver comprovantes anexados"
            data-testid={`comprovante-cell-${paymentType}-${paymentIndex}`}
          >
            <Paperclip className="w-3 h-3" /> {items.length}
          </button>
        ) : (
          <span className="text-[10px] text-gray-400">—</span>
        )}
        {canUpload && (
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
            title="Anexar comprovante"
            data-testid={`comprovante-upload-${paymentType}-${paymentIndex}`}
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.heic,.heif,application/pdf,image/*"
          onChange={handleFile}
          className="hidden"
        />
      </div>

      {showList && items.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setShowList(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md p-4" onClick={e => e.stopPropagation()}>
            <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
              <Paperclip className="w-4 h-4 text-blue-600" />
              Comprovantes ({items.length})
            </h4>
            <div className="space-y-1.5 max-h-80 overflow-y-auto">
              {items.map(c => (
                <div key={c.id} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700/30 rounded">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{c.original_filename}</p>
                    <p className="text-[10px] text-gray-500">
                      Por {c.uploaded_by_name} · {new Date(c.created_at).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <button
                    onClick={() => setVisualizandoId(c.id)}
                    className="p-1.5 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 rounded"
                    title="Ver"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  {canDelete && (
                    <button
                      onClick={() => handleDelete(c.id)}
                      className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 rounded"
                      title="Remover"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button onClick={() => setShowList(false)} className="mt-3 w-full px-3 py-2 bg-gray-200 dark:bg-gray-600 rounded text-sm hover:bg-gray-300">
              Fechar
            </button>
          </div>
        </div>
      )}

      {visualizandoId && (
        <VisualizarBlob fileId={visualizandoId} onClose={() => setVisualizandoId(null)} />
      )}
    </>
  );
}


export function VisualizarBlob({ fileId, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [contentType, setContentType] = useState('');
  const [filename, setFilename] = useState('comprovante');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let revokeUrl = null;
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${API}/comprovantes/${fileId}/download`, { responseType: 'blob' });
        if (cancelled) return;
        const ct = resp.headers['content-type'] || 'application/octet-stream';
        const url = URL.createObjectURL(resp.data);
        revokeUrl = url;
        const cd = resp.headers['content-disposition'] || '';
        const m = cd.match(/filename="([^"]+)"/);
        if (m) setFilename(m[1]);
        setContentType(ct);
        setBlobUrl(url);
      } catch (e) {
        toast.error('Erro ao carregar arquivo');
        onClose();
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; if (revokeUrl) URL.revokeObjectURL(revokeUrl); };
  }, [fileId]);

  const isPdf = contentType.includes('pdf');
  const isImg = contentType.startsWith('image/');

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="font-bold text-sm truncate">{filename}</h3>
          <div className="flex items-center gap-2">
            {blobUrl && <a href={blobUrl} download={filename} className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700">Download</a>}
            <button onClick={onClose} className="hover:bg-gray-200 dark:hover:bg-gray-700 p-1.5 rounded">✕</button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-gray-100 dark:bg-gray-900 flex items-center justify-center min-h-[400px]">
          {loading ? (
            <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
          ) : isImg && blobUrl ? (
            <img src={blobUrl} alt={filename} className="max-w-full max-h-[80vh] object-contain" />
          ) : isPdf && blobUrl ? (
            <iframe src={blobUrl} title={filename} className="w-full h-[80vh] border-0" />
          ) : (
            <a href={blobUrl} download={filename} className="px-4 py-2 bg-blue-600 text-white rounded">Baixar arquivo</a>
          )}
        </div>
      </div>
    </div>
  );
}

export default ComprovanteCell;
